import { type PixelCanvas, sprite } from '../../../lib';
import { burst, frames3, frames4, muzzle, paint, shot, type Tone, under } from './shared';

/**
 * The Censer's shots (the Plague Doctor set's gun, ADR-0160 amended): a vial of miasma tumbling end over end on a trail of
 * violet smoke, a puff of it out of the muzzle, the vial bursting into a violet cloud with glass glinting out of it; and
 * the crow its knock-out flourish lets fly. Outline-free (effects are the brightest things on screen), no team keys.
 */

type Puff = readonly [x: number, y: number, r: number];

/**
 * Smoke: the first of `puffs` (x, y, r) that holds (x, y), lit on its top-left rim (pink), violet, purple in its
 * bottom-right; `old` (0..1) dims it as it thins out (less pink, more purple).
 */
function smoke(x: number, y: number, puffs: readonly Puff[], old = 0): Tone {
  for (const [px, py, r] of puffs) {
    const [dx, dy] = [x - px, y - py];
    const d = Math.hypot(dx, dy);
    if (d >= r) continue;
    const toward = (dx + dy) / Math.max(d, 0.01);
    if (d > r - 1.2 && toward < -0.5) return old >= 0.4 ? 'p' : 'i';
    if (toward > 0.3 - old * 0.8 && d > r * (0.45 - old * 0.3)) return 'P';
    return 'p';
  }
  return null;
}

// --- The vial in flight ----------------------------------------------------------------------------------------------

/** A vial of miasma at (21, 8) turned by `a`: glass round violet liquid, its neck and cork; a glint toward the light. */
function vial(x: number, y: number, a: number): Tone {
  const [dx, dy] = [x - 21, y - 8];
  const [s, w] = [dx * Math.cos(a) + dy * Math.sin(a), -dx * Math.sin(a) + dy * Math.cos(a)];
  const lit = dx + dy < -1.5;
  // Its round body.
  const body = ((s + 0.8) / 3.3) ** 4 + (w / 2.8) ** 4;
  if (body <= 1) {
    if (body > 0.5) return lit ? 'w' : '5';
    return lit ? 'i' : dx + dy > 1.5 ? 'P' : 'p';
  }
  // Its neck, then the cork.
  if (s > 1.8 && s <= 3.8 && Math.abs(w) <= 1.1) return lit ? 'w' : '5';
  if (s > 3.8 && s <= 5.4 && Math.abs(w) <= 1.5) return lit ? 't' : 'm';
  return null;
}

const tumbling = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const a = f * ((Math.PI * 2) / 3) - 0.4;
    const glass = vial(x, y, a);
    if (glass) return glass;
    // Its trail: puffs of miasma curling behind it, smaller and thinner the older.
    const puffs: Puff[] = [0, 1, 2, 3].map((k) => {
      const px = 15.5 - k * 4.2 - (f % 3) * 0.7;
      return [px, 8 + Math.sin(px * 0.55 + f * 2.1) * 1.5, 3 - k * 0.6] as const;
    });
    return x < 18 ? smoke(x, y, puffs, x < 9 ? 0.8 : 0.4) : null;
  });

// --- The muzzle's puff -----------------------------------------------------------------------------------------------

const PUFFS: readonly (readonly Puff[])[] = [
  [
    [11, 20, 4.8],
    [17, 18, 3.8],
    [16, 23.5, 3.2],
  ],
  [
    [15, 19, 5.8],
    [23, 16.5, 4.6],
    [21, 24.5, 4.2],
    [28, 20.5, 3.2],
  ],
  [
    [22, 17.5, 3.6],
    [30, 15, 2.8],
    [27, 25.5, 2.6],
    [35, 21, 2],
  ],
];

const puff = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    if (f === 0) {
      const flash = under(Math.hypot(x - 6, y - 20), [
        ['w', 2.6],
        ['i', 4.2],
      ]);
      if (flash) return flash;
    }
    return smoke(x, y, PUFFS[f] ?? [], [0, 0.2, 0.7][f]);
  });

// --- The burst -------------------------------------------------------------------------------------------------------

/** Glass flung out of it: [angle, how far out on each frame]. */
const SHARDS = [
  [-2.6, 1],
  [-1.5, 1.15],
  [-0.4, 0.95],
  [0.7, 1.1],
  [1.8, 0.9],
  [2.8, 1.05],
] as const;

const bursting = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const [dx, dy] = [x - 22, y - 22];
    const d = Math.hypot(dx, dy);
    // The glass's glints, flying out.
    for (const [a, k] of SHARDS) {
      const r = [7, 12.5, 16.5, 19][f] as number;
      const [gx, gy] = [22 + Math.cos(a) * r * k, 22 + Math.sin(a) * r * k];
      const g = Math.hypot(x - gx, y - gy);
      if (f === 0 ? g < 1.6 : f < 3 ? g < 1.1 : g < 0.6) return f < 2 ? 'w' : '5';
    }
    if (f === 0)
      return under(d, [
        ['w', 2.8],
        ['i', 5],
        ['p', 8.5],
      ]);
    // The cloud: puffs round its middle, swelling, then drifting apart and thinning to wisps.
    const [ring, r, old] = [
      [6, 5.6, 0],
      [10, 5.4, 0.3],
      [14, 3, 0.8],
    ][f - 1] as [number, number, number];
    const puffs: Puff[] = [0, 1, 2, 3, 4, 5].map((k) => {
      const a = (k / 6) * Math.PI * 2 + f * 0.3;
      return [22 + Math.cos(a) * ring, 22 + Math.sin(a) * ring * 0.85, r - (k % 2) * 0.8] as const;
    });
    if (f === 1) puffs.push([22, 22, 6.5]);
    return smoke(x, y, puffs, old);
  });

// --- A crow, for the flourish ----------------------------------------------------------------------------------------

type Pt = readonly [number, number];
/** Whether (x, y) is inside the polygon `pts`. */
function inside(pts: readonly Pt[], x: number, y: number): boolean {
  let into = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i] as Pt;
    const [xj, yj] = pts[j] as Pt;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) into = !into;
  }
  return into;
}

/** The near wing per frame, from the shoulder: its tip and where its trailing edge meets the body (up, level, down, level). */
const WINGS: readonly (readonly [tip: Pt, trail: Pt])[] = [
  [
    [10.5, 0.5],
    [4.5, 7],
  ],
  [
    [1, 5],
    [4.5, 11],
  ],
  [
    [9.5, 19.5],
    [4.5, 13],
  ],
  [
    [1, 5],
    [4.5, 11],
  ],
];

/** The crow on frame `f` at (x, y), before its rim. */
function crowAt(f: number, x: number, y: number): Tone {
  const [tip, trail] = WINGS[f] ?? (WINGS[0] as readonly [Pt, Pt]);
  // The near wing over the body: black feathers, the violet sheen along its leading edge, its tip's fingers notched.
  const shoulder: Pt = [15.5, 9];
  // Its trailing edge from the tip back to the body, three fingers notched into it.
  const along = (k: number, notch: number): Pt => [
    tip[0] + (trail[0] - tip[0]) * k + notch,
    tip[1] + (trail[1] - tip[1]) * k,
  ];
  const wing: Pt[] = [shoulder, tip, along(0.2, 1.5), along(0.3, 0), along(0.5, 1.5), along(0.6, 0), trail, [11, 11]];
  if (inside(wing, x, y)) {
    const [ax, ay] = [tip[0] - shoulder[0], tip[1] - shoulder[1]];
    const lead = Math.abs(ay * (x - shoulder[0]) - ax * (y - shoulder[1])) / Math.hypot(ax, ay);
    return lead < 1 ? 'i' : lead < 2.2 ? 'p' : lead < 3.4 ? 'P' : '1';
  }
  // The beak: a grey wedge, lit on top.
  if (x >= 22 && x < 27 && Math.abs(y - 9 - (x - 22) * 0.12) < 1.6 - (x - 22) * 0.3) return y < 9.3 ? '5' : '3';
  // The head, an eye glinting violet.
  if (Math.hypot(x - 20.6, y - 8.6) < 0.9) return 'i';
  if (Math.hypot(x - 19.8, y - 9) < 3.2) return x + y < 26.5 ? '2' : '1';
  // The body, its back's sheen, its belly dark.
  const body = ((x - 13) / 7) ** 2 + ((y - 11 + (x - 13) * 0.12) / 3.6) ** 2;
  if (body < 1) return y < 9.5 - (x - 13) * 0.12 ? 'P' : y > 12.5 ? 'k' : '1';
  // The tail, fanned out behind.
  if (x > 1 && x < 7.5 && Math.abs(y - 12 - (7.5 - x) * 0.2) < 1.2 + (7.5 - x) * 0.4) return y < 11.5 ? '2' : 'k';
  return null;
}

/** The crow, lit along every top edge (its back, its wing's top, its head: violet, the head and beak slate) for the night. */
const crow = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const tone = crowAt(f, x, y);
    if (tone === null || crowAt(f, x, y - 1) !== null) return tone;
    return tone === '5' || tone === '3' ? '5' : Math.hypot(x - 19.8, y - 9) < 3.4 ? '4' : tone === 'i' ? 'i' : 'p';
  });

export default [
  sprite({
    name: 'censer_shot',
    ...shot,
    notes: `The Censer's bullet (ADR-0160 amended): a glass vial of miasma (the stock pellet's size: a round body of violet
liquid in glass, its neck, a cork) tumbling end over end (a third of a turn a frame), puffs of violet smoke curling
behind it, dimming to purple. Head right. Rubric (4×): S4 R4 P5 L4 C4 A4 F4. Weakest: the vial is 11 px, a pale
glint at the trail's head at 1:1.`,
    anims: { idle: { fps: 12, frames: frames3(tumbling) } },
  }),
  sprite({
    name: 'censer_muzzle',
    ...muzzle,
    notes: `The Censer's muzzle flash (ADR-0160 amended): a pink-white pop at the mouth, then a puff of violet smoke billowing
out, drifting apart and dimming over 3 frames. Points right. Rubric (4×): S4 R4 P5 L4 C4 A4 F4.`,
    anims: { flash: { fps: 30, loop: false, frames: frames3(puff) } },
  }),
  sprite({
    name: 'censer_burst',
    ...burst,
    notes: `The Censer's hit (ADR-0160 amended): the vial smashes (a pink-white flash, glass glinting out of it), a cloud of
violet miasma swells round it, opens into a ring and drifts apart in wisps as the glass flies on. 4 frames. Rubric (4×):
S4 R4 P5 L4 C4 A4 F4. Weakest: the first frame's glints ring the flash like petals.`,
    anims: { burst: { fps: 20, loop: false, frames: frames4(bursting) } },
  }),
  sprite({
    name: 'plague_bit',
    group: 'fx',
    size: [28, 20],
    origin: [14, 10],
    density: 4,
    notes: `A crow (the Plague Doctor's knock-out flourish, ADR-0160 amended): side on, facing right, black with a violet sheen
on its back and its wing's leading edge, lit along every top edge (violet; its head and beak slate) so it reads on the
night's ground too, a grey beak, an eye glinting; its near wing flaps (up, level, down, level).
28×20 art px, 7×5 world px; origin its middle. Rubric (4×): S4 R4 P4 L4 C4 A4 F4. Weakest: the head is dark on dark
but for its rim and eye; at 1:1 a crow is a black V with a violet edge, read by its flap and its company.`,
    anims: { idle: { fps: 14, frames: frames4(crow) } },
  }),
];
